import React from 'react'
import { Picture, TypographyV2 as Typography } from '@which/seatbelt'

import { AffiliateDisclaimer } from '../AffiliateDisclaimer'
import styles from './Author.module.scss'

export const Author: React.FC<Props> = ({
  jobTitle,
  name,
  src,
  sources,
  about,
  isMobile,
  renderDisclaimer = true,
}) => (
  <div className={styles.author} data-testid="author-card">
    <div className={styles.authorRowContainer}>
      <div className={styles.authorImage} data-testid="author-image">
        {renderImage({ name, src, sources })}
        {isMobile && (
          <Typography
            data-testid="author-name"
            textStyle="sb-text-heading-medium"
            className={styles.authorTitleMobile}
          >
            {renderByLine(name, jobTitle)}
          </Typography>
        )}
      </div>
      <div className={styles.authorAboutWrapper}>
        {!isMobile && (
          <Typography
            data-testid="author-name"
            textStyle="sb-text-heading-medium"
            className={styles.authorTitleDesktop}
          >
            {renderByLine(name, jobTitle)}
          </Typography>
        )}
        {about && (
          <Typography
            className={styles.authorAbout}
            data-testid="author-job-title"
            textStyle="sb-text-body-default-regular"
          >
            {about}
          </Typography>
        )}
      </div>
    </div>
    {!isMobile && renderDisclaimer && (
      <div className={styles.authorRowContainer}>
        <div className={styles.authorImage} style={{ border: 'none' }}>
          <div className={styles.authorRoundel} />
        </div>
        <div className={styles.authorAboutWrapper}>
          <div className={styles.authorDisclaimer}>
            <AffiliateDisclaimer />
          </div>
        </div>
      </div>
    )}
  </div>
)

///////// IMPLEMENTATION /////////

export interface Props extends PicksAuthor {
  isMobile: boolean
  renderDisclaimer?: boolean
}

export interface PicksAuthor {
  jobTitle?: string
  name?: string
  src?: string
  sources?: {
    srcset?: string
    media?: string
    sizes?: string
    type?: string
  }[]
  about?: string
}

const renderByLine = (name?: string, jobTitle?: string): string => {
  return `Page curated by ${name}${jobTitle ? ` - ${jobTitle}` : ''}`
}

const renderImage: React.FC<Omit<PicksAuthor, 'jobTitle' | 'about'>> = ({ src, sources, name }) =>
  src ? (
    <Picture
      aspectRatioMain="one-to-one"
      alt={`picture of ${name}`}
      className={styles.authorRoundel}
      src={src}
      sources={sources}
    />
  ) : (
    <Typography
      tag="span"
      textStyle="sb-text-interface-body-small-regular"
      className={styles.authorRoundelText}
      data-testid="author-roundel-text"
    >
      {getInitials(name)}
    </Typography>
  )

const getInitials = (fullName: string | undefined) => {
  if (!fullName?.length) {
    return fullName
  }

  const allNames = fullName?.split(' ')
  const [firstName] = allNames
  const lastName = allNames[allNames.length - 1]

  return `${firstName[0]}${lastName[0]}`.toUpperCase()
}
